import type { IAreaNode } from '@gem-sdk/heatmap-react';

import { useHeatmapAreaClickContext, useHeatmapDataContext } from '@gem-sdk/heatmap-react';
import { BlockStack, Box, Button, InlineStack, Text } from '@shopify/polaris';
import { useCallback, memo, forwardRef } from 'react';
import { DeleteIcon } from '@shopify/polaris-icons';
import { GI18NText } from '~/presentation/common';
import { useTranslation } from 'react-i18next';
import { GDiv } from '~/presentation/ui';

interface ClickAreaItemProps {
  rank: number;
  area: IAreaNode;
}

export const ClickAreaItem = memo(
  forwardRef<HTMLDivElement, ClickAreaItemProps>(({ area }, ref) => {
    const { id, selector, totalclicks, clickDist, color } = area;

    const isSelected = useHeatmapAreaClickContext((s) => s.selectedArea?.id === id);
    const setSelectedArea = useHeatmapAreaClickContext((s) => s.setSelectedArea);
    const removeArea = useHeatmapAreaClickContext((s) => s.removeArea);
    const removeClickArea = useHeatmapDataContext((s) => s.removeClickArea);

    const { t } = useTranslation();

    const handleClick = useCallback(() => {
      setSelectedArea(isSelected ? null : area);
    }, [isSelected, area, setSelectedArea]);

    const handleDelete = useCallback(() => {
      removeArea(id);
      removeClickArea(id);
    }, [id, removeArea, removeClickArea]);

    return (
      <GDiv
        ref={ref}
        className="Heatmap-Area_Item"
        data-selected={isSelected}
        onClick={handleClick}
      >
        <Box padding="300" overflowX="hidden">
          <InlineStack blockAlign="start" gap="200" wrap={false} align="space-between">
            <Box maxWidth="100%" overflowX="hidden">
              <InlineStack blockAlign="start" gap="200" wrap={false}>
                {/* Color Indicator */}
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: 'var(--p-border-radius-200)',
                    backgroundColor: color,
                    border: '1px solid var(--p-color-border)',
                    flexShrink: 0,
                  }}
                  title={`Click distribution: ${clickDist.toFixed(2)}%`}
                />
                {/* <RankBadge rank={rank} tone="light" /> */}
                <Box maxWidth="100%" overflowX="hidden">
                  <BlockStack gap="100">
                    <Text as="p" truncate variant="bodyMd" fontWeight="semibold">
                      {selector || t('Unnamed')}
                    </Text>
                    <BlockStack gap="050">
                      {/* <Text as="p" variant="bodySm" tone="subdued">
                  {getKindLabel()}
                </Text> */}
                      <GI18NText
                        as="p"
                        variant="bodyMd"
                        fontWeight="regular"
                        truncate
                        transformers={{
                          1: (text) => (
                            <Text as="span" variant="bodyMd" fontWeight="semibold">
                              {text}
                            </Text>
                          ),
                          2: (text) => (
                            <Text as="span" variant="bodyMd" fontWeight="semibold">
                              {text}
                            </Text>
                          ),
                        }}
                      >
                        {t('[1]{{count}}[] clicks · [2]{{percentage}}%[] of total', {
                          count: totalclicks,
                          percentage: clickDist.toFixed(2),
                        })}
                      </GI18NText>
                    </BlockStack>
                  </BlockStack>
                </Box>
              </InlineStack>
            </Box>

            <Button
              icon={DeleteIcon}
              variant="plain"
              tone="critical"
              size="slim"
              onClick={handleDelete}
              accessibilityLabel={t('Delete area')}
            />
          </InlineStack>
        </Box>
      </GDiv>
    );
  }),
);
